/**
 * The EV Post-Close rate-lock forms — request, confirm, cancel — built to
 * FORM_STYLE_GUIDE.md on the shared FormPrimitives.
 *
 * One set of forms, two hosts:
 *   • the drilldown sidebar (`DrilldownSidebarPostCloseLock`, `variant="sidebar"`)
 *     — the acquisitions Loan Lock tab opens these the same way the pre-close
 *     tab opens `LoanLockFormV2`;
 *   • a modal in the Lock Queue's Post-Close lane (`PostCloseLockPanel`,
 *     `variant="dialog"`), which has no drilldown sidebar to open.
 *
 * Every form takes `{ busy, onClose, onSubmit }`; `onSubmit` receives exactly
 * the body the `/pricing/post_close/*` routes take (postCloseLockApi). Confirm
 * and cancel take `variant`; the REQUEST form is on the panel-form standard
 * (§12b) and takes `frame` instead, so the sidebar can pin its footer.
 */
import { useCallback, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import isNil from "lodash/isNil";

import Txt from "../../../../components/typography/Txt";
import {
  FormDateField,
  FormField,
  FormFooter,
  FormNote,
  FormOptionChips,
  FormPanelFooter,
  FormSection,
  FormSectionDivider,
  FormSelectField,
  FormTextField,
  FormWarn,
} from "../../../../components/forms/FormPrimitives";
import {
  PanelSections,
  countRequiredFilled,
  isFilled,
  requiredOf,
} from "../../../../components/forms/panelFormModel";
import { FORM, formSx, px } from "../../../../theme/formStyles";
import { formatNumber } from "../../../../utils/numbers/formatNumber";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import {
  LOCK_CHANNELS,
  defaultLockChannelForDeal,
  lockChannelOf,
} from "../../../../api/losLocksApi";
import {
  POST_CLOSE_DSCR_MARGIN_TIERS,
  SF_COUNTERPARTY_CODES,
  SF_LOCK_PERIOD_DAYS,
  postCloseLockApi,
} from "../../../../api/postCloseLockApi";

/* ── helpers ── */
const numOrUndef = (v) => {
  if (v === "" || isNil(v)) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const dropUndefined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/**
 * Body + footer, in either host. The sidebar body scrolls with the footer
 * stuck to its bottom; the dialog gets the modal shell's padding and a ruled
 * footer.
 */
const Shell = ({ variant, children, footer }) =>
  variant === "dialog" ? (
    <>
      <Box sx={formSx.shellBody}>{children}</Box>
      {footer}
    </>
  ) : (
    <Box sx={formSx.sidebarBody}>
      {children}
      <Box sx={{ height: px(FORM.s.s6) }} />
      {footer}
    </Box>
  );

Shell.propTypes = {
  variant: PropTypes.oneOf(["sidebar", "dialog"]),
  children: PropTypes.node,
  footer: PropTypes.node,
};

/** The lock's notes, read-only, in a gray box. */
export const PostCloseLockNotes = ({ notes }) =>
  notes ? (
    <FormField label="Lock notes">
      <Box
        sx={{
          ...formSx.body,
          fontWeight: 500,
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          background: "#F8FAFC",
          border: `1px solid ${FORM.color.divider}`,
          borderRadius: px(FORM.radius.control),
          p: "12px 14px",
        }}
      >
        {notes}
      </Box>
    </FormField>
  ) : null;

PostCloseLockNotes.propTypes = { notes: PropTypes.string };

const PreviewRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: px(FORM.s.s3),
      py: px(6),
      borderBottom: `1px solid ${FORM.color.divider}`,
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    <Txt sx={formSx.meta}>{label}</Txt>
    <Txt
      sx={{
        ...formSx.body,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        color: isNil(value) ? FORM.color.ink4 : FORM.color.ink,
      }}
    >
      {isNil(value) ? "—" : value}
    </Txt>
  </Box>
);

PreviewRow.propTypes = { label: PropTypes.node, value: PropTypes.node };

/* ═════════════════════════════ REQUEST ═════════════════════════════ */
// On the panel-form standard (FORM_STYLE_GUIDE.md §12b), the same shape as the
// pre-close LockRequestForm: Price inputs (three columns), Sizer (required),
// Lock options, then the unsaved Price preview.

const SIZER_WARN = (
  <FormWarn>
    <strong>No sizer attached.</strong> The lock can&apos;t be requested
    without one. Upload the sizer workbook to continue.
  </FormWarn>
);

const requestSections = ({ isRtl, ctx, preview, previewing, runPreview }) => [
  {
    key: "price",
    title: "Price inputs",
    helper: isRtl
      ? "RTL price inputs. Rates are entered as percents."
      : "DSCR price inputs. Rates are entered as percents.",
    fields: isRtl
      ? [
          {
            name: "coupon_rate",
            label: "Coupon rate",
            kind: "percent",
            span: 2,
            req: true,
          },
          {
            name: "pricing_base_rate",
            label: "Pricing base rate",
            kind: "percent",
            span: 2,
          },
          {
            name: "pricing_spread",
            label: "Pricing spread",
            kind: "percent",
            span: 2,
          },
          {
            name: "risk_spread_adjustment",
            label: "Risk spread adj.",
            kind: "percent",
            span: 2,
            helperText: "Signed: + adds risk, − reduces.",
          },
          {
            name: "override_buy_rate",
            label: "Override buy rate",
            kind: "percent",
            span: 2,
            optional: true,
          },
        ]
      : [
          {
            name: "rate_at_lock",
            label: "Rate at lock",
            kind: "percent",
            span: 2,
            req: true,
          },
          {
            name: "base_price_for_rate",
            label: "Base price for rate",
            kind: "number",
            span: 2,
            helperText: "Bond points, par = 100.",
          },
          {
            name: "margin_tier",
            label: "Margin tier",
            kind: "select",
            span: 2,
            options: POST_CLOSE_DSCR_MARGIN_TIERS,
          },
          {
            name: "calculated_dscr_at_lock",
            label: "Calculated DSCR at lock",
            kind: "number",
            span: 2,
            req: true,
            helperText: isNil(ctx?.dscr)
              ? undefined
              : `Salesforce DSCR: ${formatNumber(ctx.dscr)}.`,
          },
        ],
  },
  {
    key: "sizer",
    title: "Sizer",
    helper:
      "Required to request a lock. Stored with the lock (and parsed for DSCR). It does not fill the fields above.",
    fields: [
      {
        name: "sizer",
        label: "Sizer workbook",
        kind: "file",
        span: 6,
        req: true,
        accept: ".xlsx",
        docGroup: "Pricing",
        docType: "Sizer",
        hint: ".xlsx",
      },
    ],
    extra: ({ ctx: c }) => (isFilled(c.values.sizer) ? null : SIZER_WARN),
  },
  {
    key: "lock",
    title: "Lock options",
    helper: "Period, lock type and notes.",
    fields: [
      {
        name: "lock_period_days",
        label: "Lock period",
        kind: "number",
        suffix: "days",
        span: 2,
        optional: true,
      },
      {
        name: "lock_channel",
        label: "Lock type",
        kind: "chips",
        span: 6,
        options: LOCK_CHANNELS,
        helperText:
          "Correspondent locks carry the margin / exit price to the correspondent.",
      },
      {
        name: "lock_notes",
        label: "Lock notes",
        kind: "textarea",
        span: 6,
        optional: true,
      },
    ],
  },
  {
    key: "preview",
    title: "Price preview",
    helper: "Prices the inputs above without saving anything.",
    showCount: false,
    fields: [],
    extra: () => (
      <Box sx={{ display: "flex", flexDirection: "column", gap: px(FORM.s.s3) }}>
        <Box>
          <Button
            variant="outlined"
            color="gray"
            onClick={runPreview}
            disabled={previewing}
            sx={formSx.button}
          >
            {previewing ? "Pricing…" : "Compute"}
          </Button>
        </Box>
        {preview?._error ? <FormWarn>{preview._error}</FormWarn> : null}
        {preview && !preview._error ? (
          <Box sx={formSx.listGroup}>
            <Box sx={{ px: px(FORM.s.s3) }}>
              {(isRtl
                ? [
                    ["Buy rate", preview.lock_buy_rate, "pct"],
                    ["Rate spread", preview.lock_rate_spread, "pct"],
                  ]
                : [
                    ["Exit price", preview.total_exit_price, "num"],
                    ["Premium points", preview.lock_total_premium_points, "num"],
                    ["Margin", preview.actual_margin, "num"],
                    [
                      "Corr. exit price",
                      preview.exit_price_to_correspondent,
                      "num",
                    ],
                  ]
              ).map(([k, v, kind]) => (
                <PreviewRow
                  key={k}
                  label={k}
                  value={
                    isNil(v)
                      ? null
                      : kind === "pct"
                        ? formatPercent(v)
                        : formatNumber(v)
                  }
                />
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    ),
  },
];

/**
 * `frame({ body, footer })` places the two: the drilldown sidebar pins the
 * footer in a panel pane; without one (the Lock Queue modal) they stack in
 * the dialog shell.
 */
export const PostCloseRequestForm = ({
  product,
  ctx,
  busy,
  onClose,
  onSubmit,
  frame,
}) => {
  const isRtl = product === "rtl";
  // Lock type defaults the way the backend would: the opp's origination
  // channel, else the portal default. The DSCR seeds from Salesforce, which is
  // what the request fell back to when the field was left blank.
  const [values, setValues] = useState(() => ({
    lock_channel: defaultLockChannelForDeal({
      originationChannel: ctx?.originationChannel,
    }),
    calculated_dscr_at_lock: isRtl ? null : (ctx?.dscr ?? null),
    sizer: [],
  }));
  const setValue = useCallback((name, v) => {
    // Lock type always holds a channel — clicking the selected chip keeps it.
    if (name === "lock_channel" && !v) return;
    setValues((prev) => ({ ...prev, [name]: v }));
  }, []);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const file = values.sizer?.[0]?.file || null;

  // FLAT price inputs (spread at the top level of the submission / preview).
  // Percent fields already hold decimal fractions (0.085 = 8.5%).
  const priceInputs = () =>
    dropUndefined(
      isRtl
        ? {
            coupon_rate: values.coupon_rate ?? undefined,
            pricing_base_rate: values.pricing_base_rate ?? undefined,
            pricing_spread: values.pricing_spread ?? undefined,
            risk_spread_adjustment: values.risk_spread_adjustment ?? undefined,
            override_buy_rate: values.override_buy_rate ?? undefined,
          }
        : {
            rate_at_lock: values.rate_at_lock ?? undefined,
            base_price_for_rate: numOrUndef(values.base_price_for_rate),
            margin_tier: values.margin_tier || undefined,
            calculated_dscr_at_lock:
              numOrUndef(values.calculated_dscr_at_lock) ??
              ctx?.dscr ??
              undefined,
          },
    );

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const res = await postCloseLockApi.pricePreview({
        product,
        lock_channel: values.lock_channel,
        ...priceInputs(),
      });
      setPreview(res);
    } catch (e) {
      setPreview({
        _error: e?.response?.data?.detail || e?.message || "Preview failed.",
      });
    } finally {
      setPreviewing(false);
    }
  };

  const sections = requestSections({
    isRtl,
    ctx,
    preview,
    previewing,
    runPreview,
  });
  const fctx = { values };
  const required = requiredOf(sections, fctx);
  const requiredFilled = countRequiredFilled(required, values);
  const canSubmit = requiredFilled === required.length && !!file;
  // Why the primary is off, said on the button itself — the footer only counts.
  const blockedReason = !file
    ? "Attach a sizer to request this lock"
    : !canSubmit
      ? "Fill in the required price inputs"
      : null;

  const submit = () => {
    if (!canSubmit) return;
    const submission = dropUndefined({
      product,
      lock_period_days: numOrUndef(values.lock_period_days),
      lock_channel: values.lock_channel || undefined,
      lock_notes: values.lock_notes || undefined,
      ...priceInputs(),
      uploadMetadata: [
        { id: file.name, docType: "Sizer", docGroup: "Pricing" },
      ],
    });
    onSubmit({ submission, file });
  };

  const body = (
    <PanelSections sections={sections} ctx={fctx} setValue={setValue} />
  );

  const primary = (
    <Button onClick={submit} disabled={busy || !canSubmit} sx={formSx.button}>
      {busy ? "Requesting…" : "Request lock"}
    </Button>
  );

  const footer = (
    <FormPanelFooter
      required={required.length}
      filled={requiredFilled}
      readyText="Ready to send"
    >
      <Button
        variant="outlined"
        color="gray"
        onClick={onClose}
        disabled={busy}
        sx={formSx.button}
      >
        Cancel
      </Button>
      {blockedReason && !busy ? (
        <Tooltip title={blockedReason} placement="top" arrow>
          {/* A disabled button fires no events — the span carries the hover. */}
          <span>{primary}</span>
        </Tooltip>
      ) : (
        primary
      )}
    </FormPanelFooter>
  );

  return frame ? (
    frame({ body, footer })
  ) : (
    <>
      <Box sx={formSx.shellBody}>{body}</Box>
      {footer}
    </>
  );
};

PostCloseRequestForm.propTypes = {
  product: PropTypes.oneOf(["dscr", "rtl"]),
  ctx: PropTypes.object,
  busy: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  /** ({ body, footer }) → node. Absent → the dialog shell. */
  frame: PropTypes.func,
};

/* ═════════════════════════════ CONFIRM ═════════════════════════════ */

export const PostCloseConfirmForm = ({
  lock,
  busy,
  onClose,
  onSubmit,
  variant = "sidebar",
}) => {
  const [f, setF] = useState(() => ({
    lock_period_days: lock?.lock_period_days ?? null,
    lock_channel: lockChannelOf(lock) ?? defaultLockChannelForDeal(null),
  }));
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = () => {
    // The backend REPLACES lock_notes with what is sent, so append the desk's
    // note to the requester's rather than wiping it.
    const note = (f.lock_notes || "").trim();
    const existing = (lock?.lock_notes || "").trim();
    onSubmit(
      dropUndefined({
        lock_counterparty: f.lock_counterparty || undefined,
        lock_period_days: numOrUndef(f.lock_period_days),
        lock_channel: f.lock_channel || undefined,
        lock_expiration_date: f.lock_expiration_date || undefined,
        lock_notes: note
          ? existing
            ? `${existing}\n${note}`
            : note
          : undefined,
      }),
    );
  };

  return (
    <Shell
      variant={variant}
      footer={
        <FormFooter sticky={variant !== "dialog"}>
          <Button
            variant="text"
            color="gray"
            onClick={onClose}
            disabled={busy}
            sx={formSx.button}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={submit}
            disabled={busy}
            sx={formSx.button}
          >
            {busy ? "Confirming…" : "Confirm lock"}
          </Button>
        </FormFooter>
      }
    >
      <FormNote sx={{ mb: px(FORM.s.s5) }}>
        Confirming sets the counterparty and expiration and writes a
        Rate_Lock__c to Salesforce. Give a lock period or an explicit expiration
        date.
      </FormNote>

      <FormSection n={1} title="Counterparty & expiration">
        <Box sx={formSx.fields}>
          <FormSelectField
            label="Counterparty"
            value={f.lock_counterparty}
            onChange={set("lock_counterparty")}
            options={SF_COUNTERPARTY_CODES}
            helperText="Salesforce code. Only recognized codes map to Salesforce."
          />
          <Box sx={formSx.grid2}>
            <FormSelectField
              label="Lock period"
              value={f.lock_period_days}
              onChange={set("lock_period_days")}
              options={SF_LOCK_PERIOD_DAYS.map((d) => ({
                value: d,
                label: `${d} days`,
              }))}
              helperText="Salesforce accepts 30, 45 or 60."
            />
            {/* FormDateField holds a partly typed year as a draft instead of
                committing it (typing "2" used to land the year in 1902); the
                route takes a plain YYYY-MM-DD. */}
            <FormDateField
              label="Expiration date"
              value={f.lock_expiration_date}
              onChange={(iso) =>
                set("lock_expiration_date")(iso ? iso.slice(0, 10) : null)
              }
              helperText="Overrides the period."
            />
          </Box>
          <FormField label="Lock type">
            <FormOptionChips
              options={LOCK_CHANNELS}
              value={f.lock_channel}
              onChange={(v) => v && set("lock_channel")(v)}
            />
          </FormField>
        </Box>
      </FormSection>

      <FormSectionDivider />

      <FormSection n={2} title="Notes">
        <Box sx={formSx.fields}>
          <PostCloseLockNotes notes={lock?.lock_notes} />
          <FormTextField
            label={lock?.lock_notes ? "Add a note" : "Notes"}
            multiline
            minRows={3}
            value={f.lock_notes}
            onChange={set("lock_notes")}
            helperText={
              lock?.lock_notes
                ? "Added below the existing lock notes; it does not replace them."
                : undefined
            }
          />
        </Box>
      </FormSection>
    </Shell>
  );
};

PostCloseConfirmForm.propTypes = {
  lock: PropTypes.object,
  busy: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  variant: PropTypes.oneOf(["sidebar", "dialog"]),
};

/* ═════════════════════════════ CANCEL ══════════════════════════════ */

export const PostCloseCancelForm = ({
  lock,
  busy,
  onClose,
  onSubmit,
  variant = "sidebar",
}) => {
  const [reason, setReason] = useState("");
  return (
    <Shell
      variant={variant}
      footer={
        <FormFooter sticky={variant !== "dialog"}>
          <Button
            variant="text"
            color="gray"
            onClick={onClose}
            disabled={busy}
            sx={formSx.button}
          >
            Keep lock
          </Button>
          <Button
            color="error"
            onClick={() =>
              onSubmit(dropUndefined({ reason: reason.trim() || undefined }))
            }
            disabled={busy}
            sx={formSx.button}
          >
            {busy ? "Cancelling…" : "Cancel lock"}
          </Button>
        </FormFooter>
      }
    >
      <Box sx={formSx.fields}>
        <FormWarn>
          <strong>This cancels the active lock on this loan.</strong> A
          Rate_Lock__c already pushed to Salesforce is not deleted.
        </FormWarn>
        <PostCloseLockNotes notes={lock?.lock_notes} />
        <FormTextField
          label="Reason"
          multiline
          minRows={3}
          value={reason}
          onChange={setReason}
          placeholder="Why is this lock being cancelled?"
        />
      </Box>
    </Shell>
  );
};

PostCloseCancelForm.propTypes = {
  lock: PropTypes.object,
  busy: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  variant: PropTypes.oneOf(["sidebar", "dialog"]),
};

/** Sidebar / dialog title for each mode. */
export const POST_CLOSE_FORM_TITLES = {
  request: "Request post-close lock",
  update: "Update lock request",
  relock: "Re-lock",
  confirm: "Confirm post-close lock",
  cancel: "Cancel post-close lock",
};

/** Fired after a post-close lock mutation so an open panel reloads. */
export const POST_CLOSE_LOCK_CHANGED = "postCloseLock:changed";
